import { useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { apiFetch, type MeResponse } from './api'
import Placeholder from './pages/Placeholder'
import UsersTab from './pages/UsersTab'
import PayrollSubmissions from './pages/PayrollSubmissions'
import Scheduler from './pages/Scheduler'
import ApAutoForward from './pages/ApAutoForward'
import Signalbox from './pages/Signalbox'
import FinancialYearsTab from './pages/admin/FinancialYearsTab'
import NominalsTab from './pages/admin/NominalsTab'
import DepartmentsTab from './pages/admin/DepartmentsTab'
import CostCentresTab from './pages/admin/CostCentresTab'
import DiagnosticUploadTab from './pages/admin/DiagnosticUploadTab'

/**
 * Top-level tabs.
 *
 * PUBLIC CONTRACT: these path slugs appear in browser URLs and bookmarks.
 * Add new ones freely; never rename a shipped one.
 */
interface Tab {
  to: string
  label: string
  blurb: string
  /** Has a real page. Tabs without this get the Placeholder route. */
  real?: boolean
  /** Hidden from the tab bar and unroutable unless the caller is an admin. */
  adminOnly?: boolean
}

const TABS: Tab[] = [
  { to: '/',                    label: 'Overview',            blurb: 'Headline financial position for the railway.' },
  { to: '/management-accounts', label: 'Management Accounts', blurb: 'Monthly management accounts and variance to budget.' },
  { to: '/budgets',             label: 'Budgets',             blurb: 'Departmental budgets and forecast reprojections.' },
  { to: '/cashflow',            label: 'Cashflow',            blurb: 'Cash position, receipts and payment runs.' },
  { to: '/transactions',        label: 'Transactions',        blurb: 'Ledger detail and transaction search.' },
  { to: '/payroll-submissions', label: 'Payroll Submissions', blurb: 'Monthly payroll journals, posted to iplicit as drafts.', real: true },
  { to: '/signalbox',           label: 'Signalbox',           blurb: 'Recurring finance work by period — who owns it, and whether it is done.', real: true },
  { to: '/iplicit-auto-forward', label: 'iplicit Auto Forward', blurb: 'Supplier invoices from the finance mailbox into iplicit.', real: true, adminOnly: true },
  { to: '/scheduler',           label: 'Scheduler',           blurb: 'Scheduled jobs, their history and their settings.', real: true, adminOnly: true },
]

function useCurrentUser() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // 1. Is there a SWA session? If not, drive the login ourselves.
        const authRes = await fetch('/.auth/me')
        const auth = (await authRes.json()) as { clientPrincipal: unknown | null }
        if (!auth.clientPrincipal) {
          const returnTo = window.location.pathname + window.location.search
          window.location.replace(
            `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`,
          )
          return
        }

        // 2. Role comes from our API (the DB), never from /.auth/me userRoles.
        const data = await apiFetch<MeResponse>('/api/me')
        if (!cancelled) setMe(data)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { me, loading, error }
}

function Header({ me }: { me: MeResponse }) {
  const roleLabel = me.role === 'none' ? 'no access' : me.role
  return (
    <header className="app-header">
      <h1>NYMR Finance</h1>
      <div className="user-chip">
        <span>{me.displayName ?? me.upn}</span>
        <span className="role-pill" data-role={me.role}>{roleLabel}</span>
        <a href="/logout">Sign out</a>
      </div>
    </header>
  )
}

function TabBar({ me, tabs }: { me: MeResponse; tabs: typeof TABS[number][] }) {
  const pill = ({ isActive }: { isActive: boolean }) =>
    `tab-pill${isActive ? ' active' : ''}`
  const adminPill = ({ isActive }: { isActive: boolean }) =>
    `tab-pill admin${isActive ? ' active' : ''}`

  return (
    <nav className="tab-bar" aria-label="Sections">
      <div className="tab-bar__tabs">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.to === '/'} className={pill}>
            {t.label}
          </NavLink>
        ))}
      </div>
      {me.isAdmin && (
        <NavLink to="/admin" className={adminPill}>Admin</NavLink>
      )}
    </nav>
  )
}

function AdminPage({ me }: { me: MeResponse }) {
  const tab = ({ isActive }: { isActive: boolean }) =>
    `sub-tab${isActive ? ' active' : ''}`

  return (
    <>
      <nav className="sub-tab-bar" aria-label="Admin sections">
        <NavLink to="/admin/users" className={tab}>Users</NavLink>
        <NavLink to="/admin/financial-years" className={tab}>Financial years</NavLink>
        <NavLink to="/admin/nominals" className={tab}>Nominals</NavLink>
        <NavLink to="/admin/departments" className={tab}>Departments</NavLink>
        <NavLink to="/admin/cost-centres" className={tab}>Cost centres</NavLink>
        <NavLink to="/admin/diagnostic-upload" className={tab}>Payroll PDF Diagnostic Upload</NavLink>
      </nav>
      <Routes>
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<UsersTab me={me} />} />
        <Route path="financial-years" element={<FinancialYearsTab />} />
        <Route path="nominals" element={<NominalsTab />} />
        <Route path="departments" element={<DepartmentsTab />} />
        <Route path="cost-centres" element={<CostCentresTab />} />
        <Route path="diagnostic-upload" element={<DiagnosticUploadTab />} />
        <Route path="*" element={<Navigate to="/admin/users" replace />} />
      </Routes>
    </>
  )
}

function NoAccess({ me }: { me: MeResponse }) {
  return (
    <div className="gate">
      <h2>You don't have access yet</h2>
      <p>
        You're signed in as <strong>{me.upn}</strong>, but a Finance admin hasn't
        given you an access level yet.
      </p>
      <p className="muted">Ask Chris Flynn or Simon Cooper to grant you access.</p>
      <p><a href="/logout">Sign out</a></p>
    </div>
  )
}

export default function App() {
  const { me, loading, error } = useCurrentUser()

  if (loading) return <div className="gate"><p>Signing you in…</p></div>

  if (error) {
    return (
      <div className="gate">
        <h2>Something went wrong</h2>
        <p className="muted">{error}</p>
        <p><a href="/logout">Sign out and try again</a></p>
      </div>
    )
  }

  // useCurrentUser redirects to Entra when there's no session, so a null `me`
  // here means the navigation is already in flight.
  if (!me) return <div className="gate"><p>Redirecting to sign in…</p></div>

  if (!me.hasAccess) {
    return (
      <>
        <Header me={me} />
        <main className="app-main"><NoAccess me={me} /></main>
      </>
    )
  }

  // A tab is available when it isn't admin-only (or you're an admin) AND it isn't
  // hidden for you personally (Admin → Users → Tabs). A hidden/off-limits tab
  // loses its pill AND its route; the fallback route sends any such URL to the
  // first tab the user CAN see. Guard with `?? []` so a pre-feature /api/me
  // response (no hiddenTabs) shows everything.
  const hidden = me.hiddenTabs ?? []
  const visibleTabs = TABS.filter((t) => (!t.adminOnly || me.isAdmin) && !hidden.includes(t.to))
  const home = visibleTabs[0]?.to ?? null

  return (
    <>
      <Header me={me} />
      <TabBar me={me} tabs={visibleTabs} />
      <main className="app-main">
        {visibleTabs.length === 0 && !me.isAdmin ? (
          <div className="gate">
            <h2>No tabs are available to you</h2>
            <p className="muted">A Finance admin has restricted your access. Ask them to enable a tab.</p>
            <p><a href="/logout">Sign out</a></p>
          </div>
        ) : (
          <Routes>
            {/* Placeholder tabs (no real page yet) that this user can see. */}
            {visibleTabs.filter((t) => !t.real).map((t) => (
              <Route
                key={t.to}
                path={t.to}
                element={<Placeholder title={t.label} blurb={t.blurb} />}
              />
            ))}
            {/* Real pages — routed only when the tab is visible to this user, so
                a hidden or admin-only tab is unreachable by URL as well as pill. */}
            {visibleTabs.some((t) => t.to === '/payroll-submissions') && (
              <Route path="/payroll-submissions" element={<PayrollSubmissions me={me} />} />
            )}
            {visibleTabs.some((t) => t.to === '/signalbox') && (
              <Route path="/signalbox" element={<Signalbox me={me} />} />
            )}
            {visibleTabs.some((t) => t.to === '/iplicit-auto-forward') && (
              <Route path="/iplicit-auto-forward" element={<ApAutoForward />} />
            )}
            {visibleTabs.some((t) => t.to === '/scheduler') && (
              <Route path="/scheduler" element={<Scheduler />} />
            )}
            <Route
              path="/admin/*"
              element={me.isAdmin ? <AdminPage me={me} /> : <Navigate to={home ?? '/'} replace />}
            />
            {/* Admins with every tab hidden still land somewhere they can use. */}
            <Route path="*" element={<Navigate to={home ?? '/admin/users'} replace />} />
          </Routes>
        )}
      </main>
      {/* Signalbox composes a printable sheet into this, and its @media print
          block hides #root — so only the sheet reaches the printer. */}
      <div id="tm-printsheet" />
    </>
  )
}
